import React, { ComponentProps, ForwardedRef, forwardRef, memo } from 'react'
import { theme } from 'styled'

const UNIT_CIRCLE = 31.4
const CIRCLE_DEGREES = 360

const getStrokeWidth = (numberOfLines: number, total: number) => {
  const percent = numberOfLines / total
  return percent * UNIT_CIRCLE
}

const getRotationOffset = (currentLength: number) => {
  return (currentLength / UNIT_CIRCLE) * CIRCLE_DEGREES
}

export interface SvgStatCircleProps extends ComponentProps<'svg'> {
  error?: number
  total?: number
  warning?: number
  galvanized?: number
}

const StatCircleSvg = forwardRef(
  (
    {
      error = 0,
      total = 100,
      warning = 0,
      galvanized = 0,
      ...rest
    }: SvgStatCircleProps,
    ref: ForwardedRef<SVGSVGElement>
  ) => {
    const known = total - error - warning - galvanized
    const knownLength = getStrokeWidth(known, total)
    const errorLength = getStrokeWidth(error, total)
    const warningLength = getStrokeWidth(warning, total)
    const galvanizedLength = getStrokeWidth(galvanized, total)

    return (
      <svg height="20%" ref={ref} viewBox="0 0 20 20" width="20%" {...rest}>
        <g fill="none" strokeWidth="10" transform="rotate(-90, 10,10)">
          <circle
            cx="10"
            cy="10"
            r="5"
            stroke={theme.colors.primary}
            strokeDasharray={`${knownLength} ${UNIT_CIRCLE}`}
          />
          <circle
            cx="10"
            cy="10"
            r="5"
            stroke="#F7685B"
            strokeDasharray={`${errorLength} ${UNIT_CIRCLE}`}
            transform={`rotate(${getRotationOffset(knownLength)}, 10,10)`}
          />
          <circle
            cx="10"
            cy="10"
            r="5"
            stroke="#0033A0"
            strokeDasharray={`${warningLength} ${UNIT_CIRCLE}`}
            transform={`rotate(${getRotationOffset(
              knownLength + errorLength
            )}, 10,10)`}
          />
          <circle
            cx="10"
            cy="10"
            r="5"
            stroke="#FFC72C"
            strokeDasharray={`${galvanizedLength} ${UNIT_CIRCLE}`}
            transform={`rotate(${getRotationOffset(
              knownLength + errorLength + warningLength
            )}, 10,10)`}
          />
        </g>
        <circle cx="10" cy="10" fill={theme.colors.white} r="9" />
      </svg>
    )
  }
)

StatCircleSvg.displayName = 'StatCircleSvg'

const SvgStatCircle = memo(StatCircleSvg, (prevProps, nextProps) => {
  const isSameGalvanized = prevProps.galvanized === nextProps.galvanized
  const isSameWarning = prevProps.warning === nextProps.warning
  const isSameError = prevProps.error === nextProps.error
  const isSameTotal = prevProps.total === nextProps.total
  return isSameError && isSameWarning && isSameTotal && isSameGalvanized
})

export default SvgStatCircle
